/**
 * Redux slice for users using consistent API response format
 * This will gradually replace the legacy usersSlice.js
 */

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import userService from "../services/userService";
import { User, ApiResponse, PaginationInfo, ResourceAccess } from "../types/apiTypes";
import { handleApiError } from "../utils/consistentApiParser";

// Async thunks
export const getUsers = createAsyncThunk(
  "users/getUsers",
  async (params: { page?: number; perPage?: number } = {}, { rejectWithValue }) => {
    try {
      if (process.env.NODE_ENV === "development") {
        console.log("usersSlice: Calling userService.getPaginated()");
      }

      const response = await userService.getPaginated(params.page, params.perPage);

      if (process.env.NODE_ENV === "development") {
        console.log("usersSlice: Received response from service:", response);
      }

      return response;
    } catch (error) {
      console.error("usersSlice: Error fetching users:", error);
      const errorMessage = handleApiError(error, "consistentUsersSlice", "getUsers");
      return rejectWithValue(errorMessage);
    }
  },
);

export const getUser = createAsyncThunk("users/getUser", async (id: string, { rejectWithValue }) => {
  try {
    const response = await userService.getById(id);
    return response;
  } catch (error) {
    const errorMessage = handleApiError(error, "consistentUsersSlice", "getUser");
    return rejectWithValue(errorMessage);
  }
});

export const createUser = createAsyncThunk("users/createUser", async (userData: Partial<User>, { rejectWithValue }) => {
  try {
    if (process.env.NODE_ENV === "development") {
      console.log("usersSlice: Creating user with data:", userData);
    }

    const response = await userService.create(userData);

    if (process.env.NODE_ENV === "development") {
      console.log("usersSlice: User created successfully:", response);
    }

    return response;
  } catch (error) {
    console.error("usersSlice: Error creating user:", error);
    const errorMessage = handleApiError(error, "consistentUsersSlice", "createUser");
    return rejectWithValue(errorMessage);
  }
});

export const updateUser = createAsyncThunk(
  "users/updateUser",
  async ({ id, userData }: { id: string; userData: Partial<User> }, { rejectWithValue }) => {
    try {
      const response = await userService.update(id, userData);
      return response;
    } catch (error) {
      const errorMessage = handleApiError(error, "consistentUsersSlice", "updateUser");
      return rejectWithValue(errorMessage);
    }
  },
);

export const deleteUser = createAsyncThunk("users/deleteUser", async (id: string, { rejectWithValue }) => {
  try {
    await userService.remove(id);
    return { user_id: id };
  } catch (error) {
    const errorMessage = handleApiError(error, "consistentUsersSlice", "deleteUser");
    return rejectWithValue(errorMessage);
  }
});

export const searchUsers = createAsyncThunk(
  "users/searchUsers",
  async (params: { query: string; page?: number; perPage?: number }, { rejectWithValue }) => {
    try {
      const response = await userService.search(params.query, params.page, params.perPage);
      return response;
    } catch (error) {
      const errorMessage = handleApiError(error, "consistentUsersSlice", "searchUsers");
      return rejectWithValue(errorMessage);
    }
  },
);

export const getConsumers = createAsyncThunk(
  "users/getConsumers",
  async (params: { page?: number; perPage?: number } = {}, { rejectWithValue }) => {
    try {
      const response = await userService.getByRole("consumer", params.page, params.perPage);
      return response;
    } catch (error) {
      const errorMessage = handleApiError(error, "consistentUsersSlice", "getConsumers");
      return rejectWithValue(errorMessage);
    }
  },
);

export const batchUpdateUsers = createAsyncThunk(
  "users/batchUpdateUsers",
  async (updates: Array<{ id: string; data: Partial<User> }>, { rejectWithValue }) => {
    try {
      const response = await userService.batchUpdate(updates);
      return response;
    } catch (error) {
      const errorMessage = handleApiError(error, "consistentUsersSlice", "batchUpdateUsers");
      return rejectWithValue(errorMessage);
    }
  },
);

export const batchDeleteUsers = createAsyncThunk(
  "users/batchDeleteUsers",
  async (userIds: string[], { rejectWithValue }) => {
    try {
      const response = await userService.batchDelete(userIds);
      return response;
    } catch (error) {
      const errorMessage = handleApiError(error, "consistentUsersSlice", "batchDeleteUsers");
      return rejectWithValue(errorMessage);
    }
  },
);

export const getUserPermissions = createAsyncThunk(
  "users/getUserPermissions",
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await userService.getUserPermissions(userId);
      return response;
    } catch (error) {
      const errorMessage = handleApiError(error, "usersSlice", "getUserPermissions");
      return rejectWithValue(errorMessage);
    }
  },
);

export const setUserPermission = createAsyncThunk(
  "users/setUserPermission",
  async (
    { userId, resourceId, resourceType }: { userId: string; resourceId: string; resourceType: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await userService.setUserPermission(userId, resourceId, resourceType);
      return response;
    } catch (error) {
      const errorMessage = handleApiError(error, "usersSlice", "setUserPermission");
      return rejectWithValue(errorMessage);
    }
  },
);

export const removeUserPermission = createAsyncThunk(
  "users/removeUserPermission",
  async ({ userId, resourceId }: { userId: string; resourceId: string }, { rejectWithValue }) => {
    try {
      await userService.removeUserPermission(userId, resourceId);
      return { userId, resourceId };
    } catch (error) {
      const errorMessage = handleApiError(error, "usersSlice", "removeUserPermission");
      return rejectWithValue(errorMessage);
    }
  },
);

// State interface
interface ConsistentUsersState {
  users: User[];
  currentUser: User | null;
  loading: boolean;
  error: string | null;
  success: boolean;
  pagination: PaginationInfo | null;
  message: string | null;
  searchResults: User[];
  searchLoading: boolean;
  searchError: string | null;
  userPermissions: ResourceAccess[];
  userPermissionsLoading: boolean;
  userPermissionsError: string | null;
}

const initialState: ConsistentUsersState = {
  users: [],
  currentUser: null,
  loading: false,
  error: null,
  success: false,
  pagination: null,
  message: null,
  searchResults: [],
  searchLoading: false,
  searchError: null,
  userPermissions: [],
  userPermissionsLoading: false,
  userPermissionsError: null,
};

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.searchError = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    clearMessage: (state) => {
      state.message = null;
    },
    setCurrentUser: (state, action: PayloadAction<User | null>) => {
      state.currentUser = action.payload;
    },
    clearCurrentUser: (state) => {
      state.currentUser = null;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Users
      .addCase(getUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        const response = action.payload as ApiResponse<User[]>;
        state.loading = false;
        state.users = response.data || [];
        state.pagination = response.pagination;
        state.message = response.message;

        if (process.env.NODE_ENV === "development") {
          console.log("usersSlice: getUsers fulfilled with users:", state.users);
        }
      })
      .addCase(getUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Get User
      .addCase(getUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUser.fulfilled, (state, action) => {
        const response = action.payload as ApiResponse<User>;
        state.loading = false;
        state.currentUser = response.data;
        state.message = response.message;
      })
      .addCase(getUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Create User
      .addCase(createUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        const response = action.payload as ApiResponse<User>;
        state.loading = false;
        state.success = true;
        state.message = response.message;

        if (response.data) {
          state.users.push(response.data);
        }
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      })

      // Update User
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        const response = action.payload as ApiResponse<User>;
        state.loading = false;
        state.success = true;
        state.message = response.message;

        if (response.data) {
          const index = state.users.findIndex((user) => user.user_id === response.data!.user_id);
          if (index !== -1) {
            state.users[index] = response.data;
          }

          if (state.currentUser?.user_id === response.data.user_id) {
            state.currentUser = response.data;
          }
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      })

      // Delete User
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = "User deleted successfully";

        const userId = action.payload.user_id;
        state.users = state.users.filter((user) => user.user_id !== userId);

        if (state.currentUser?.user_id === userId) {
          state.currentUser = null;
        }
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      })

      // Search Users
      .addCase(searchUsers.pending, (state) => {
        state.searchLoading = true;
        state.searchError = null;
      })
      .addCase(searchUsers.fulfilled, (state, action) => {
        const response = action.payload as ApiResponse<User[]>;
        state.searchLoading = false;
        state.searchResults = response.data || [];
      })
      .addCase(searchUsers.rejected, (state, action) => {
        state.searchLoading = false;
        state.searchError = action.payload as string;
      })

      // Get Consumers (users with CONSUMER role)
      .addCase(getConsumers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getConsumers.fulfilled, (state, action) => {
        const response = action.payload as ApiResponse<User[]>;
        state.loading = false;
        state.users = response.data || [];
        state.pagination = response.pagination;
        state.message = response.message;
      })
      .addCase(getConsumers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Get user resource permissions
      .addCase(getUserPermissions.pending, (state) => {
        state.userPermissionsLoading = true;
        state.userPermissionsError = null;
      })
      .addCase(getUserPermissions.fulfilled, (state, action) => {
        state.userPermissionsLoading = false;
        const response = action.payload as ApiResponse<ResourceAccess[]>;
        state.userPermissions = response.data || [];
      })
      .addCase(getUserPermissions.rejected, (state, action) => {
        state.userPermissionsLoading = false;
        state.userPermissionsError = action.payload as string;
      })

      // Set user resource access
      .addCase(setUserPermission.pending, (state) => {
        state.userPermissionsLoading = true;
        state.userPermissionsError = null;
      })
      .addCase(setUserPermission.fulfilled, (state, action) => {
        state.userPermissionsLoading = false;
        const response = action.payload as ApiResponse<ResourceAccess>;
        if (response.data) {
          const index = state.userPermissions.findIndex((c) => c.resource_id === response.data!.resource_id);
          if (index !== -1) {
            state.userPermissions[index] = response.data;
          } else {
            state.userPermissions.push(response.data);
          }
        }
      })
      .addCase(setUserPermission.rejected, (state, action) => {
        state.userPermissionsLoading = false;
        state.userPermissionsError = action.payload as string;
      })

      // Remove user resource access
      .addCase(removeUserPermission.pending, (state) => {
        state.userPermissionsLoading = true;
        state.userPermissionsError = null;
      })
      .addCase(removeUserPermission.fulfilled, (state, action) => {
        state.userPermissionsLoading = false;
        state.userPermissions = state.userPermissions.filter((c) => c.resource_id !== action.payload.resourceId);
      })
      .addCase(removeUserPermission.rejected, (state, action) => {
        state.userPermissionsLoading = false;
        state.userPermissionsError = action.payload as string;
      });
  },
});

export const { clearError, clearSuccess, clearMessage, setCurrentUser, clearCurrentUser, clearSearchResults } =
  usersSlice.actions;

export default usersSlice.reducer;
